import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { State } from 'reducers';
import { CallId, VideoCallOfferEvent, VideoCallButtonEvent } from 'shared/models';
import { getVideoCall, getVideoCallEvents, getVideoCallEventsError, isLoadingEvents } from 'selectors/ecsSelectors';
import { getVideoCallEventsRequest } from 'actions/ecsActions';
import { animated, useSpring, useSprings } from 'react-spring';
import { VideoCallEventRecord } from '../videoCallEventRecord/VideoCallEventRecord';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';

require('./VideoCallEventRecordSet.css');

export type Props = {
    callId: CallId;
    visible: boolean;
};

export const VideoCallEventRecordSet = (props: Props) => {
    const { callId, visible } = props;
    const { t } = useTranslation();

    const videoCall = useSelector((state: State) => getVideoCall(state, props));
    const events = useSelector((state: State) => getVideoCallEvents(state, props));
    const loading = useSelector((state: State) => isLoadingEvents(state, props));
    const error = useSelector(getVideoCallEventsError);

    const eventIds = useMemo(() => {
        return (events || [])
            // Filter out technical events
            .map((e, i) => ({ e, i }))
            .filter(({ e }) =>
                (e.type !== 'JPEG') &&
                (e.type !== 'ICEE') &&
                (e.type !== 'ICES') &&
                (e.type !== 'ANSR') &&
                (e.type !== 'RREC') &&
                (e.type !== 'OFFR' || (e.details as VideoCallOfferEvent['details']).seq === 0) &&
                (e.type !== 'BTTN' || (
                    (e.details as VideoCallButtonEvent['details']).yes === true ||
                    (e.details as VideoCallButtonEvent['details']).no === true ||
                    (e.details as VideoCallButtonEvent['details']).help === true
                ))
            )
            .map(({ i }) => i);
    }, [ events ]);

    const springs = useSprings(eventIds.length, eventIds.map(() => ({
        config: { mass: 1, tension: 750, friction: 45 },
        from: {
            opacity: 0,
            height: '0em',
        },
        to: {
            opacity: 1,
            height: '2.5em',
        },
        reverse: !visible,
    })));

    const listStyle = useSpring({
        config: { mass: 1, tension: 300, friction: 35 },
        from: { pct: 0 },
        to: { pct: 100 },
        delay: visible ? 0 : 250,
        reverse: !visible,
    });

    const loadingStyle = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        reverse: !loading || !visible,
    });

    const dispatch = useDispatch();

    const updated = videoCall ? videoCall.updated : 0;

    useEffect(() => {
        if (visible && callId && !events && !loading && !error) {
            dispatch(getVideoCallEventsRequest(callId));
        }
    }, [ events, loading, updated, callId, dispatch, visible, error ]);


    if (!videoCall) return null;
    return (
        <animated.div
            className="vcers"
            style={ {
                ...listStyle,
                padding: listStyle.pct.to(p => `${p / 100}em 1.5em`),
            } }
        >
            <animated.div
                style={ {
                    ...loadingStyle,
                    position: 'absolute',
                    padding: '0 .75em',
                } }
            >
                <span style={ { lineHeight: '2.5em', verticalAlign: 'middle' } }>
                    <LoadingSpinner size="tiny"/>
                </span>
                <span style={ { paddingLeft: '.5em', lineHeight: '2.5em', opacity: .75 } }>
                    { t('LOADING') }
                </span>
            </animated.div>
            
            { events && springs.map((style, i) =>
                <animated.div
                    key={ i.toString() }
                    style={ style }
                >
                    <VideoCallEventRecord
                        callId={ callId }
                        { ...events[eventIds[i]] }
                    />
                </animated.div>
            ) }
        </animated.div>
    );
};
